import React, {useEffect, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import ReactLoading from 'react-loading';
import Header from './Header';
import NavBar from './NavBar';
import {SetterOrUpdater, useRecoilState} from 'recoil';
import {
  IBaseUrlType,
  IUserInfo,
  baseUrlTypeState,
  userInfoState,
} from 'utils/Atoms';
import {NavigateFunction, useLocation, useNavigate} from 'react-router-dom';
import {getDevice} from 'utils/getDevice';
import CommonModal from './CommonModal';
import {useFormatMsg} from 'utils/useFormatMsg';
import {minmax} from 'utils/minmax';
import {useSessionTimeout} from 'utils/useSessionTimeout';

/**
 * 화면 레이아웃 컴포넌트의 Props 인터페이스
 * @property {boolean} [isLoading] - 로딩 상태
 * @property {React.ReactNode} children - 자식 요소
 * @property {(event?: any) => void} [onClick] - 클릭 이벤트 핸들러
 * @property {boolean} [noHeader] - 헤더 표시 여부
 * @property {any} [errorModal] - 에러 모달 정보
 */
interface IScreenLayout {
  isLoading?: boolean;
  children: React.ReactNode;
  onClick?: (event?: any) => void;
  noHeader?: boolean;
  errorModal?: any;
}

interface ILogout {
  setUserInfo: SetterOrUpdater<IUserInfo | null>;
  navigate: NavigateFunction;
  setBaseUrlType: SetterOrUpdater<IBaseUrlType>;
}

/**
 * 로그아웃 함수
 * 사용자 정보를 초기화하고 로그인 페이지로 이동합니다.
 * @param {ILogout} params - 로그아웃 파라미터
 * @param {SetterOrUpdater<IUserInfo | null>} params.setUserInfo - 사용자 정보 설정 함수
 * @param {NavigateFunction} params.navigate - 네비게이션 함수
 * @param {SetterOrUpdater<IBaseUrlType>} params.setBaseUrlType - 베이스 URL 타입 설정 함수
 * @returns {void}
 */
export const logout = ({setUserInfo, navigate, setBaseUrlType}: ILogout) => {
  localStorage.clear();
  sessionStorage.clear();
  setUserInfo(null);
  setBaseUrlType('dev');
  navigate('/', {replace: true});
};

/**
 * 화면 레이아웃 컴포넌트
 * 전체 화면 레이아웃을 관리하는 컴포넌트입니다.
 * @param {IScreenLayout} props - 화면 레이아웃 컴포넌트의 props
 * @returns {JSX.Element} 화면 레이아웃 컴포넌트
 */
const ScreenLayout = ({
  children,
  isLoading,
  onClick,
  noHeader,
  errorModal,
}: IScreenLayout) => {
  const [errorModalboolen, setErrorModalboolen] = useState(false);

  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [baseUrlType, setBaseUrlType] = useRecoilState(baseUrlTypeState);

  const location = useLocation();
  const navigate = useNavigate();

  useSessionTimeout();

  const [isLoadingHeader, setIsLoadingHeader] = useState(false);
  const [headerErrorModal, setHeaderErrorModal] = useState({});

  const [errorModalInfo, setErrorModalInfo] = useState({...errorModal});

  useEffect(() => {
    if (Object.keys(headerErrorModal).length) {
      setErrorModalInfo(headerErrorModal);
      setErrorModalboolen(true);
    }
  }, [headerErrorModal]);

  const isPC = getDevice() === 'pc' ? true : false;

  const handleModal = () => {
    if (
      errorModal.status === 'GoHome' ||
      errorModal.status === 'GoOut' ||
      errorModalInfo.status === 'NoPermission' ||
      errorModalInfo.status === 'GoHome'
    ) {
      logout({
        setUserInfo,
        navigate,
        setBaseUrlType,
      });
    } else {
      setErrorModalboolen(false);
      setHeaderErrorModal({});
    }
  };

  const title =
    errorModalInfo?.status === 'GoOut'
      ? errorModalInfo?.title
      : useFormatMsg(errorModalInfo?.title);
  const detail =
    errorModalInfo?.status === 'GoOut'
      ? errorModalInfo?.detail
      : useFormatMsg(errorModalInfo?.detail);
  const confirm = useFormatMsg('confirm');
  const startTime = errorModalInfo?.startTime;
  const endTime = errorModalInfo?.endTime;
  const childStatusHistoryList = errorModalInfo?.childStatusHistoryList;

  useEffect(() => {
    if (errorModal?.onOff) {
      setErrorModalboolen(true);
    }
    setErrorModalInfo(errorModal);
  }, [errorModal]);

  useEffect(() => {
    const pathName = window.location.pathname;
    const userInfolocalStorage = JSON.parse(
      localStorage.getItem('userInfo') ?? 'null',
    );
    const userInfoSessionStorage = JSON.parse(
      sessionStorage.getItem('userInfo') ?? 'null',
    );

    const goToMain = ['/', '/resetPassword', '/register'];
    const goToLogin = ['/main', '/notice'];

    for (const path of goToMain) {
      if (
        path === pathName &&
        (userInfolocalStorage !== null || userInfoSessionStorage !== null)
      ) {
        navigate('/main');
      }
    }

    for (const path of goToLogin) {
      if (
        path === pathName &&
        userInfolocalStorage == null &&
        userInfoSessionStorage == null
      ) {
        navigate('/');
      }
    }
  }, []);

  return (
    <Wrapper onClick={onClick}>
      {(isLoadingHeader || isLoading) && (
        <Loading>
          <ReactLoading
            type={'spin'}
            color={colors.primary}
            height={'10%'}
            width={'10%'}
          />
        </Loading>
      )}

      {noHeader ? (
        <Container style={{height: '100%', width: '100%'}}>
          {children}
        </Container>
      ) : (
        <>
          <Header />
          <NavBar />
          <Container $isPC={isPC ? 1 : 0}>{children}</Container>
        </>
      )}
      {errorModalboolen && (
        <CommonModal
          closeModal={() => handleModal()}
          title={title}
          detail={detail}
          startTime={startTime}
          endTime={endTime}
          childStatusHistoryList={childStatusHistoryList}
          firstButtonText={confirm}
          firstButtonClick={() => handleModal()}
        />
      )}
    </Wrapper>
  );
};
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  > * {
    font-family: 'Noto Sans KR', sans-serif;
  }
`;

const Loading = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(52, 52, 52, 0.5);
`;

const Container = styled.div<{$isPC?: number}>`
  display: flex;
  flex-direction: column;
  width: calc(100vw - ${minmax(312)});
  height: calc(100vh - ${minmax(88)});
  position: fixed;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;

  ${(props: any) =>
    props.$isPC == 0 &&
    css`
      width: calc(100vw - 160px);
      height: calc(
        var(--vh, 1vh) * 100 -
          max(61.599999999999994px, min(6.159999999999999vw, 88px))
      );
    `}

  ${(props: any) =>
    props.$isWide === 1 &&
    css`
      width: 100%;
      height: 100%;
    `}
`;

export default ScreenLayout;
